import * as React from 'react';
import { useState, useEffect, useRef } from 'react'; // React Hook 명시적 import
import Paper from '@mui/material/Paper';
import { Box, Stack, Zoom } from '@mui/material';
import ParentBox from 'pages/components/ParentBox';
import { repeatEffect, settingsConfig } from 'utils/commonFunc';
import ApiService from 'services/ApiService';
import BaseMapView from 'map/BaseMapView';
import CommonBoundaryLayer from 'map/layers/CommonBoundaryLayer';
import BaseTitleBox from 'pages/components/BaseTitleBox';
import ColorInfoBox from 'pages/components/ColorInfoBox';
import PlaceIcon from '@mui/icons-material/Place';
import { Heatmap as HeatmapLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';

// --- [핵심 1] 좌표 그룹화 함수 추가 ---
/**
 * 가까운 좌표들을 그룹화하고 가중치를 계산합니다.
 * @param {Array} rows - API에서 받은 데이터의 rows 배열
 * @param {number} precision - 좌표를 그룹화할 소수점 자릿수 (4 = 약 11m, 3 = 약 111m)
 * @returns {Array} - { lat, lng, weight } 형태의 그룹화된 좌표 배열
 */
const groupNearbyCoordinates = (rows, precision = 3) => {
    const groupMap = new Map();
    const factor = Math.pow(10, precision);

    rows.forEach(rowData => {
        if (rowData.route && Array.isArray(rowData.route)) {
            rowData.route.forEach(coord => {
                if (coord && coord.length >= 2) {
                    const lat = parseFloat(coord[0]);
                    const lon = parseFloat(coord[1]);

                    if (!isNaN(lat) && !isNaN(lon)) {
 
                        const roundedLat = Math.round(lat * factor) / factor;
                        const roundedLon = Math.round(lon * factor) / factor;
                        const key = `${roundedLat},${roundedLon}`;

                        if (groupMap.has(key)) {

                            groupMap.get(key).weight += 1;
                        } else {
                            groupMap.set(key, { lat: roundedLat, lng: roundedLon, weight: 1 });
                        }
                    }
                }
            });
        }
    });

    // Map의 값들을 배열로 변환하여 반환
    return Array.from(groupMap.values());
};


export default function EmergencyVehicleLocationView() {
    const mapRef = useRef();
    const [currentData, setCurrentData] = useState();
    const heatmapLayerRef = useRef(null); // 히트맵 레이어 참조 추가

    useEffect(() => {
        if (mapRef.current) {
            const map = mapRef.current.GetOlMap();
            map.addLayer(new CommonBoundaryLayer(map));
            
            const stopRepeat = repeatEffect(
                async () => {
                    try {
                        const response = await ApiService.get("newdashboard/afterpack/GetEmergLocation");
                        if (response.status === 200 && response.data.rows) {
                            const data = response.data;
                            
                            setCurrentData({
                                labels: ['낮음', '', '중간', '', '높음']
                            });
                            
                            // 기존 히트맵 레이어 제거
                            if (heatmapLayerRef.current) {
                                map.removeLayer(heatmapLayerRef.current);
                                heatmapLayerRef.current = null;
                            }

                            // precision을 3으로 조정 (약 111m 단위)
                            const groupedCoords = groupNearbyCoordinates(data.rows, 3);

                            if (groupedCoords.length > 0) {
                                const features = groupedCoords.map(coord => {
                                    const feature = new Feature({
                                        geometry: new Point(fromLonLat([coord.lng, coord.lat]))
                                    });
                                    feature.set('weight', coord.weight);
                                    return feature;
                                });

                                console.log(`원본 좌표 ${data.rows.flatMap(r => r.route).length}개 -> 그룹화된 좌표 ${features.length}개`);
                                console.log('가중치 분포:', features.map(f => f.get('weight')).sort((a,b) => b-a).slice(0, 10));
                                
                                // 개선된 가중치 정규화 방식
                                const weights = features.map(f => f.get('weight'));
                                const maxWeight = Math.max(...weights);
                                
                                // 로그 스케일 적용
                                features.forEach(f => {
                                    const originalWeight = f.get('weight');
                                    const logWeight = Math.log(originalWeight + 1) / Math.log(maxWeight + 1);
                                    f.set('weight', Math.max(0.1, logWeight)); // 최소값 0.1 보장
                                });

                                // 새로운 히트맵 레이어 생성
                                const heatmapSource = new VectorSource();
                                const heatmapLayer = new HeatmapLayer({
                                    source: heatmapSource,
                                    blur: 12,    
                                    radius: 7,
                                    opacity: 0.95,
                                    weight: (feature) => feature.get('weight'),
                                    zIndex: 1000,
                                    gradient: ['#4d7fff', '#33dada', '#33cc33', '#ffeb4d', '#ff4d4d']
                                });

                                // 피처들을 소스에 추가
                                heatmapSource.addFeatures(features);
                                
                                // 맵에 레이어 추가
                                map.addLayer(heatmapLayer);
                                heatmapLayerRef.current = heatmapLayer;
                            }
                        }
                    } catch (error) {
                        console.error('Emergency vehicle heatmap data fetch error:', error);
                    }
                },
                settingsConfig().contentUpdateDelaySec
            );
            
            return () => {
                stopRepeat();
                // 컴포넌트 언마운트 시 히트맵 레이어 정리
                if (heatmapLayerRef.current) {
                    map.removeLayer(heatmapLayerRef.current);
                    heatmapLayerRef.current = null;
                }
            };
        }
    }, [mapRef]);

    return (
        <ParentBox sx={{ position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ width: '100%', height: '100%', position: 'absolute', left: '-13%', top: '-3%' }}>
                <BaseMapView
                    ref={mapRef}
                    initViewData={{ zoom: 12.34, center: [127.06, 37.29] }}
                />
            </Box>
            <Stack sx={{ position: 'absolute', right: '0', top: '80px', width: '700px', height :'74%' }}
                direction="column">
                <Paper variant='outlined' sx={{ width:'100%', height:'100%', p: 3, borderRadius: '32px', background: 'var(--gs-90)' }}>
                    <Stack spacing={6} sx={{ pl:2, pr:2, pt:10, pb:10, height:'100%', borderRadius: '20px', justifyContent:'center', gap:'60px', }}>
                        <BaseTitleBox title={`최근 1개월 긴급차량 위치 분포도`} icon={<PlaceIcon />} sx={{borderRadius: '20px', background: 'var(--white-05)', }} />
                        <Box sx={{display:'flex', flexDirection:'column', gap:'20px'}}>
                            <Box sx={{
                                width: '100%', height: '30px',
                                background: `linear-gradient(to right, #4d7fff, #33dada, #33cc33, #ffeb4d, #ff4d4d)`,
                                borderRadius: '15px'
                            }} />
                            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
                                <span style={{fontSize:'24px'}}>낮음</span>
                                <span style={{fontSize:'24px'}}>높음</span>
                            </Stack>
                        </Box>
                    </Stack>
                </Paper>
            </Stack>
        </ParentBox>
    );
}